<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">問卷回覆</h1>
      </div>
      <div class="header-actions">
        <router-link class="btn btn-secondary" :to="{ name: 'QuestionnaireList' }">問卷列表</router-link>
        <router-link class="btn btn-secondary" :to="{ name: 'QuestionnaireStats', params: { id: $route.params.id } }">統計</router-link>
        <button type="button" class="btn btn-secondary" :disabled="exporting" @click="downloadCsv">
          {{ exporting ? '下載中' : '下載 CSV' }}
        </button>
        <button type="button" class="btn btn-danger" :disabled="clearing" @click="clearResponses">
          {{ clearing ? '清除中' : '清除回覆' }}
        </button>
        <router-link class="btn btn-primary" :to="{ name: 'EditQuestionnaire', params: { id: $route.params.id } }">編輯問卷</router-link>
      </div>
    </div>
    <div class="page-container">
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>送出時間</th>
              <th>來源</th>
              <th>身份</th>
              <th>回覆</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="items.length === 0"><td colspan="4">尚無回覆</td></tr>
            <tr
              v-for="item in items"
              :key="item.id"
              class="response-row"
              tabindex="0"
              @click="openResponse(item)"
              @keydown.enter.prevent="openResponse(item)"
            >
              <td>
                <div>{{ formatTime(item.submittedAt) }}</div>
                <div class="open-hint">查看</div>
              </td>
              <td>{{ item.source === 'flow' ? '流程' : '公開' }}</td>
              <td>
                <div v-if="item.memberName">會員：{{ item.memberName }}</div>
                <div v-if="item.lineDisplayName">好友：{{ item.lineDisplayName }}</div>
                <div v-if="item.lineUserId || item.memberId" class="identity-id">{{ item.lineUserId || item.memberId }}</div>
                <div v-if="!item.memberName && !item.lineDisplayName && !item.lineUserId && !item.memberId">未識別</div>
                <div v-if="item.identityNote">{{ item.identityNote }}</div>
                <div v-for="(value, key) in item.intake || {}" :key="key">{{ key }}：{{ value }}</div>
              </td>
              <td>
                <div v-for="line in answerLines(item)" :key="line.id" class="answer-line">
                  <strong>{{ line.prompt }}</strong>
                  <div class="pre">{{ line.text }}</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <teleport to="body">
      <div v-if="selected" class="response-detail" role="dialog" aria-modal="true" aria-labelledby="response-detail-title" @click.self="closeResponse">
        <header class="detail-bar">
          <strong id="response-detail-title">回覆內容</strong>
          <span>{{ formatTime(selected.submittedAt) }}</span>
          <button type="button" @click="closeResponse">關閉</button>
        </header>
        <main class="detail-body">
          <section class="detail-stack">
            <article class="detail-card">
              <p><span class="meta-label">來源</span>{{ selected.source === 'flow' ? '流程' : '公開' }}</p>
              <p><span class="meta-label">身份</span></p>
              <p v-if="selected.memberName">會員：{{ selected.memberName }}</p>
              <p v-if="selected.lineDisplayName">好友：{{ selected.lineDisplayName }}</p>
              <p v-if="selected.lineUserId || selected.memberId" class="identity-id">{{ selected.lineUserId || selected.memberId }}</p>
              <p v-if="!selected.memberName && !selected.lineDisplayName && !selected.lineUserId && !selected.memberId">未識別</p>
              <p v-if="selected.identityNote">{{ selected.identityNote }}</p>
              <template v-if="intakeEntries(selected).length">
                <p><span class="meta-label">帶入欄位</span></p>
                <p v-for="entry in intakeEntries(selected)" :key="entry.key">{{ entry.key }}：{{ entry.value }}</p>
              </template>
            </article>
            <article v-for="line in answerLines(selected)" :key="line.id" class="detail-card">
              <p class="prompt">{{ line.prompt }}</p>
              <p class="pre">{{ line.text }}</p>
            </article>
            <p v-if="!answerLines(selected).length" class="empty-answers">這份回覆沒有作答</p>
          </section>
        </main>
      </div>
    </teleport>
  </div>
</template>

<script>
import { questionnaireService } from '@/api/modules/questionnaires.js';
import { showError, showSuccess, showConfirmDeferred } from '@/utils/swal.js';

export default {
  name: 'QuestionnaireResponses',
  data() {
    return { items: [], exporting: false, clearing: false, selected: null };
  },
  async mounted() {
    document.addEventListener('keydown', this.onKeydown);
    const response = await questionnaireService.responses(this.$route.params.id, { page: 1, limit: 50 });
    this.items = response?.data?.items || [];
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    openResponse(item) {
      this.selected = item;
    },
    closeResponse() {
      this.selected = null;
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.closeResponse();
    },
    intakeEntries(item) {
      return Object.entries(item?.intake || {}).map(([key, value]) => ({ key, value }));
    },
    answerLines(item) {
      let rows = item?.answers || [];
      if (typeof rows === 'string') {
        try {
          rows = JSON.parse(rows);
        } catch {
          rows = [];
        }
      }
      return rows.map((answer, index) => ({
        id: answer.id || index,
        prompt: answer.prompt_snapshot || answer.promptSnapshot || '未命名題目',
        text: this.answerText(answer) || '—'
      }));
    },
    formatTime(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('zh-TW');
    },
    async clearResponses() {
      const confirmed = await showConfirmDeferred(
        '清除回覆',
        '這份問卷已收下的回覆與作答會全部刪除，且無法復原。問卷、題目與已經貼上的標籤會保留。清掉之後，同一人可以再填一次。',
        '清除',
        '取消'
      );
      if (!confirmed) return;
      this.clearing = true;
      try {
        const response = await questionnaireService.clearResponses(this.$route.params.id);
        const removed = response?.data?.removed || 0;
        this.items = [];
        await showSuccess(removed ? `已清除 ${removed} 份回覆` : '沒有回覆可清除');
      } catch (error) {
        await showError('清除失敗', error?.response?.data?.message || '無法清除回覆');
      } finally {
        this.clearing = false;
      }
    },
    async downloadCsv() {
      this.exporting = true;
      try {
        const id = this.$route.params.id;
        const [detail, items] = await Promise.all([
          questionnaireService.get(id),
          this.loadAllResponses(id)
        ]);
        if (!items.length) {
          await showError('尚無回覆');
          return;
        }
        const name = detail?.data?.name || '問卷回覆';
        this.saveCsv(this.buildCsv(detail?.data, items), `${this.safeFilename(name)}.csv`);
      } catch (error) {
        await showError('下載失敗', error?.response?.data?.message || '無法下載 CSV');
      } finally {
        this.exporting = false;
      }
    },
    async loadAllResponses(id) {
      const items = [];
      let page = 1;
      let totalPages = 1;
      while (page <= totalPages && page <= 500) {
        const response = await questionnaireService.responses(id, { page, limit: 100 });
        const data = response?.data || {};
        const batch = data.items || [];
        items.push(...batch);
        totalPages = data.pagination?.totalPages || 1;
        if (!batch.length) break;
        page += 1;
      }
      return items;
    },
    buildCsv(row, items) {
      const intakeKeys = [];
      const seenIntake = new Set();
      items.forEach((item) => {
        Object.keys(item.intake || {}).forEach((key) => {
          if (!seenIntake.has(key)) {
            seenIntake.add(key);
            intakeKeys.push(key);
          }
        });
      });
      const questions = this.questionColumns(row, items);
      const headers = [
        '送出時間',
        '來源',
        'LINE 使用者 ID',
        '會員 ID',
        '好友名稱',
        '會員名稱',
        '身份備註',
        ...intakeKeys.map((key) => `帶入：${key}`),
        ...questions.map((question) => question.header)
      ];
      const lines = [headers.map((cell) => this.csvCell(cell)).join(',')];
      items.forEach((item) => {
        const answers = new Map((item.answers || []).map((answer) => [answer.question_id, answer]));
        const cells = [
          this.formatTime(item.submittedAt),
          item.source === 'flow' ? '流程' : '公開',
          item.lineUserId || '',
          item.memberId || '',
          item.lineDisplayName || '',
          item.memberName || '',
          item.identityNote || '',
          ...intakeKeys.map((key) => item.intake?.[key] ?? ''),
          ...questions.map((question) => this.answerText(answers.get(question.id)))
        ];
        lines.push(cells.map((cell) => this.csvCell(cell)).join(','));
      });
      return lines.join('\r\n');
    },
    questionColumns(row, items) {
      const columns = [];
      const seen = new Set();
      (row?.document?.pages || []).forEach((page) => {
        (page.blocks || []).forEach((block) => {
          const question = block.type === 'question' ? block.question : null;
          if (!question?.id || seen.has(question.id)) return;
          seen.add(question.id);
          columns.push({ id: question.id, header: question.prompt || '未命名題目' });
        });
      });
      items.forEach((item) => {
        (item.answers || []).forEach((answer) => {
          if (!answer.question_id || seen.has(answer.question_id)) return;
          seen.add(answer.question_id);
          columns.push({ id: answer.question_id, header: answer.prompt_snapshot || '已刪除題目' });
        });
      });
      return columns;
    },
    answerText(answer) {
      if (!answer) return '';
      let selections = answer.selections || [];
      if (typeof selections === 'string') {
        try {
          selections = JSON.parse(selections);
        } catch {
          selections = [];
        }
      }
      const parts = [];
      if (answer.text_value) parts.push(answer.text_value);
      const labels = (Array.isArray(selections) ? selections : [])
        .map((selection) => selection.label)
        .filter((label) => label != null && String(label) !== '');
      if (labels.length) parts.push(labels.join('、'));
      if (answer.other_text) parts.push(`其他：${answer.other_text}`);
      return parts.join('；');
    },
    csvCell(value) {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    },
    safeFilename(name) {
      return String(name || '問卷回覆').replace(/[\\/:*?"<>|]/g, '_').trim() || '問卷回覆';
    },
    saveCsv(csv, filename) {
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
.table-wrapper table { min-width: 900px; }
.pre { white-space: pre-wrap; }
.answer-line { margin-bottom: 10px; }
.answer-line:last-child { margin-bottom: 0; }
.identity-id { color: #5f6368; font-size: 12px; }
.response-row { cursor: pointer; }
.response-row:hover, .response-row:focus { background: #f4f2ff; outline: none; }
.open-hint { color: #5746e3; font-size: 12px; }
.response-detail {
  position: fixed;
  inset: 0;
  z-index: 12000;
  display: flex;
  flex-direction: column;
  background: #f0ebf8;
  color: #202124;
}
.detail-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #e6e8f0;
}
.detail-bar span { flex: 1; color: #5f6368; font-size: 13px; }
.detail-bar button {
  border: 0;
  background: transparent;
  color: #5746e3;
  font: inherit;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
}
.detail-bar button:hover { background: #f4f2ff; }
.detail-body { flex: 1; overflow: auto; }
.detail-stack {
  width: min(640px, 100%);
  margin: 0 auto;
  padding: 12px 12px 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.detail-card {
  margin: 0;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}
.detail-card p { margin: 0 0 8px; }
.detail-card p:last-child { margin-bottom: 0; }
.meta-label { display: block; color: #5f6368; font-size: 12px; }
.prompt { font-weight: 600; }
.empty-answers { margin: 0; color: #5f6368; text-align: center; }
</style>
